import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button, Card, CardContent, Badge, Spinner } from "@learn-me/ui";
import { FileText, Download, ArrowLeft } from "lucide-react";
import { formatCurrency } from "@learn-me/utils";
import toast from "react-hot-toast";
import {
  materialsApi,
  MATERIAL_CATEGORY_LABELS,
} from "@/features/materials/api/materialsApi";
import { useAuthStore } from "@/features/auth/store/authStore";
import { normalizeImageUrl } from "@/lib/imageUrl";

export default function MaterialDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();

  const { data: material, isLoading } = useQuery({
    queryKey: ["material", slug],
    queryFn: () => materialsApi.getBySlug(slug!),
    enabled: !!slug,
  });

  const handleDownload = async () => {
    if (!material) return;
    try {
      await materialsApi.downloadMaterial(
        material.id,
        material.fileName || material.title,
      );
    } catch {
      toast.error("Inicia sesión y compra el material para descargarlo");
    }
  };

  const handleBuy = () => {
    if (!material) return;
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    navigate(`/checkout/material/${material.slug}`);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!material) {
    return (
      <div className="py-20 text-center">
        <h1 className="text-2xl font-bold mb-2">Material no encontrado</h1>
        <Link to="/materiales">
          <Button>Ver materiales</Button>
        </Link>
      </div>
    );
  }

  const list = material.listPrice != null ? Number(material.listPrice) : null;
  const sale = Number(material.salePrice);
  const showDiscount =
    !material.isFree && material.hasDiscount && list != null && list > sale;

  return (
    <div className="container py-10 max-w-4xl">
      <Link
        to="/materiales"
        className="inline-flex items-center text-muted-foreground hover:text-foreground mb-6"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Volver a materiales
      </Link>

      <div className="grid md:grid-cols-2 gap-8">
        <div className="aspect-video md:aspect-square bg-secondary-100 rounded-lg overflow-hidden">
          {material.coverImageUrl ? (
            <img
              src={normalizeImageUrl(material.coverImageUrl) ?? ""}
              alt={material.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <FileText className="h-20 w-20 text-secondary-400" />
            </div>
          )}
        </div>

        <div>
          <Badge variant="outline" className="mb-3">
            {MATERIAL_CATEGORY_LABELS[material.category]}
          </Badge>
          <h1 className="text-2xl font-bold mb-3">{material.title}</h1>
          {material.description && (
            <p className="text-muted-foreground mb-6 whitespace-pre-line">
              {material.description}
            </p>
          )}

          <Card>
            <CardContent className="p-5 space-y-4">
              <div className="flex items-baseline gap-3">
                {showDiscount && (
                  <span className="text-lg text-muted-foreground line-through">
                    {formatCurrency(list!, material.currency)}
                  </span>
                )}
                <span className="text-3xl font-bold">
                  {material.isFree
                    ? "Gratis"
                    : formatCurrency(sale, material.currency)}
                </span>
                {showDiscount && (
                  <Badge className="bg-red-500 text-white border-0">
                    {Math.round(((list! - sale) / list!) * 100)}% OFF
                  </Badge>
                )}
              </div>

              {material.hasAccess ? (
                <Button className="w-full" onClick={handleDownload}>
                  <Download className="h-4 w-4 mr-2" />
                  Descargar material
                </Button>
              ) : material.isFree ? (
                <Button className="w-full" onClick={handleDownload}>
                  <Download className="h-4 w-4 mr-2" />
                  Descargar gratis
                </Button>
              ) : (
                <Button className="w-full" onClick={handleBuy}>
                  Comprar material
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
